import { z } from "zod";
import { OperationSchema, type Operation } from "./operations.js";
import { IdSchema, ValueMapSchema } from "./schema.js";

/**
 * Live editing protocol (brief 8.3), Socket.IO over WebSocket at `/socket.io`, one room per map.
 *
 * Messages to client participants never carry Salesforce IDs, links, Account or Opportunity names
 * or anyone's email address (brief 8.3). Nothing here does: the staff-only context (Account,
 * Opportunity, owner, participant emails) travels over the staff REST API, not the socket. People
 * in the room are named by display name and told apart by connection ID (a random Socket.IO ID).
 */

export const OpIdSchema = z
  .string()
  .regex(
    /^[A-Za-z0-9_-]{8,64}$/,
    "Operation IDs are 8 to 64 letters, digits, hyphens or underscores",
  );

// ---------- client to server ----------

export const JoinSchema = z.object({
  mapId: IdSchema,
  /** The last revision this client holds, after a reconnect. The server still sends a snapshot. */
  lastRevision: z.number().int().min(0).optional(),
});

export const OpMessageSchema = z.object({
  opId: OpIdSchema,
  op: OperationSchema,
  /** The revision the client had applied when it made the edit. Informational: last write wins. */
  baseRevision: z.number().int().min(0),
});

/**
 * Long text fields are soft-locked to whoever is typing in them (brief 8.3, ADR-003). A field path
 * names one: `<goalId>.<field>`.
 */
export const LOCKABLE_FIELDS = [
  "rightNow",
  "objective",
  "capability",
  "outcome",
  "costInaction",
] as const;
export type LockableField = (typeof LOCKABLE_FIELDS)[number];

const FIELD_PATH = new RegExp(`^[A-Za-z0-9_-]{1,40}[.](?:${LOCKABLE_FIELDS.join("|")})$`);

export const FieldPathSchema = z.string().regex(FIELD_PATH, "Not a lockable field");

export function fieldPath(goalId: string, field: LockableField): string {
  return `${goalId}.${field}`;
}

/** The lockable field an operation writes to, or null if it writes to none. */
export function lockedFieldOf(op: Operation): string | null {
  if (op.type !== "setField" || op.goalId === undefined || op.blockId !== undefined) return null;
  return (LOCKABLE_FIELDS as readonly string[]).includes(op.path)
    ? `${op.goalId}.${op.path}`
    : null;
}

/** A lock lapses after this long without an edit from its holder (brief 8.3). */
export const LOCK_IDLE_MS = 20_000;

export const LockRequestSchema = z.object({ fieldPath: FieldPathSchema });

export const VIEWS = ["edit", "quantify", "prioritise", "present"] as const;
export const ViewSchema = z.enum(VIEWS);
export type View = z.infer<typeof ViewSchema>;

/** Where a person is in the map, sent when they change view or goal. */
export const PresenceUpdateSchema = z.object({
  view: ViewSchema,
  goalId: IdSchema.optional(),
});

/** Facilitator only: ask everyone who has not opted out to follow this person's view and goal. */
export const FollowSchema = z.object({ enabled: z.boolean() });

export type Join = z.infer<typeof JoinSchema>;
export type OpMessage = z.infer<typeof OpMessageSchema>;
export type LockRequest = z.infer<typeof LockRequestSchema>;
export type PresenceUpdate = z.infer<typeof PresenceUpdateSchema>;
export type Follow = z.infer<typeof FollowSchema>;

// ---------- server to client ----------

export const MAP_STATUSES = ["Draft", "Live workshop", "Final", "Archived"] as const;
export const MapStatusSchema = z.enum(MAP_STATUSES);
export type MapStatus = z.infer<typeof MapStatusSchema>;

export const SettingsSchema = z.object({
  /** Whether this viewer may change the map. The server enforces it on every operation. */
  canEdit: z.boolean(),
  /**
   * Whether this viewer may remove goals, value blocks, value areas and factors (staff owners and
   * editors only).
   */
  canDelete: z.boolean(),
  /** Whether this viewer may lead the workshop with follow me (staff owners and editors only). */
  canLead: z.boolean(),
  status: MapStatusSchema,
  /** The map's title. Client participants also see it in their invitation. */
  title: z.string().max(255),
});

export const SaveStateSchema = z.object({
  /** "unsaved" while Salesforce cannot be reached: every client shows a warning (brief 8.3). */
  status: z.enum(["saved", "unsaved"]),
  /** Revision last written to Salesforce, if any since the room opened. */
  revision: z.number().int().min(0),
  /** ISO 8601 time of the last successful write. */
  at: z.string().optional(),
});

/** A Socket.IO connection ID: random, and never tied to an email address or Salesforce record. */
export const ConnectionIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);

export const ROLES = ["owner", "editor", "viewer"] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

/** One open connection in the room. A person with two tabs open appears twice. */
export const PresenceEntrySchema = z.object({
  id: ConnectionIdSchema,
  name: z.string().max(120),
  kind: z.enum(["staff", "guest"]),
  /** Staff with no role on the map are viewers here. */
  role: RoleSchema,
  view: ViewSchema,
  goalId: IdSchema.optional(),
  /** This connection is leading: followers move to its view and goal. */
  leading: z.boolean(),
});

export const PresenceSchema = z.object({ participants: z.array(PresenceEntrySchema) });

export const LockHolderSchema = z.object({ id: ConnectionIdSchema, name: z.string().max(120) });

export const LockStateSchema = z.object({
  fieldPath: FieldPathSchema,
  holder: LockHolderSchema.nullable(),
  /** How long the lock lasts without another edit. Clients clear it themselves after this. */
  expiresInMs: z.number().int().min(0).optional(),
});

export const SnapshotSchema = z.object({
  map: ValueMapSchema,
  revision: z.number().int().min(0),
  settings: SettingsSchema,
  save: SaveStateSchema,
  /** This connection's ID, so the client can tell its own locks and presence from others'. */
  me: ConnectionIdSchema,
  participants: z.array(PresenceEntrySchema),
  /** Fields locked right now (the holder is never null here). */
  locks: z.array(LockStateSchema),
});

export const AppliedSchema = z.object({
  /** Present for the sender's own operations, so the sender does not apply them twice. */
  opId: OpIdSchema.optional(),
  op: OperationSchema,
  revision: z.number().int().min(0),
});

export const AckSchema = z.object({ opId: OpIdSchema, revision: z.number().int().min(0) });

export const REJECT_REASONS = [
  "not_found",
  "invalid",
  "limit",
  "duplicate",
  "read_only",
  "too_large",
  "unavailable",
  /** Someone else is typing in that field. */
  "locked",
] as const;
export const RejectReasonSchema = z.enum(REJECT_REASONS);
export type RejectReason = z.infer<typeof RejectReasonSchema>;

export const RejectSchema = z.object({ opId: OpIdSchema.optional(), reason: RejectReasonSchema });

export const SavedSchema = z.object({
  revision: z.number().int().min(0),
  at: z.string(),
});

export const UnsavedSchema = z.object({ at: z.string() });

export const ErrorMessageSchema = z.object({
  /** `revoked`: this person's access was removed. The client stops and does not reconnect. */
  code: z.enum([
    "unauthenticated",
    "forbidden",
    "revoked",
    "not_found",
    "bad_request",
    "unavailable",
  ]),
  message: z.string().max(200),
});

export type Settings = z.infer<typeof SettingsSchema>;
export type SaveState = z.infer<typeof SaveStateSchema>;
export type Snapshot = z.infer<typeof SnapshotSchema>;
export type Applied = z.infer<typeof AppliedSchema>;
export type Ack = z.infer<typeof AckSchema>;
export type Reject = z.infer<typeof RejectSchema>;
export type Saved = z.infer<typeof SavedSchema>;
export type Unsaved = z.infer<typeof UnsavedSchema>;
export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;
export type PresenceEntry = z.infer<typeof PresenceEntrySchema>;
export type Presence = z.infer<typeof PresenceSchema>;
export type LockHolder = z.infer<typeof LockHolderSchema>;
export type LockState = z.infer<typeof LockStateSchema>;

/** Typed Socket.IO event maps, shared by the server and the SPA. */
export interface ClientToServerEvents {
  join: (payload: Join) => void;
  op: (payload: OpMessage) => void;
  /** On focus of a long text field. */
  lock: (payload: LockRequest) => void;
  /** On blur, or after 20 seconds without typing. */
  unlock: (payload: LockRequest) => void;
  presence: (payload: PresenceUpdate) => void;
  follow: (payload: Follow) => void;
}

export interface ServerToClientEvents {
  snapshot: (payload: Snapshot) => void;
  applied: (payload: Applied) => void;
  ack: (payload: Ack) => void;
  reject: (payload: Reject) => void;
  saved: (payload: Saved) => void;
  unsaved: (payload: Unsaved) => void;
  presence: (payload: Presence) => void;
  lock: (payload: LockState) => void;
  error: (payload: ErrorMessage) => void;
}

/** Heartbeat so Heroku's router never sees an idle connection (it closes after about 55 seconds). */
export const HEARTBEAT_INTERVAL_MS = 25_000;
/** Largest message accepted from a client: one operation carries at most one goal or block. */
export const MAX_MESSAGE_BYTES = 128 * 1024;
